/**
 * Home's first run (LED-1151): a workspace with no projects.
 *
 * ONE centred block. The web console cannot record walks, so it never asks
 * the reader to start one — it says where walks come from instead, offers to
 * create a project, and points a Procore or Autodesk shop at the connection.
 *
 * "New project": there is no project-creation endpoint the console can call
 * yet (projects are created through the mobile app's synced session — see
 * `JobSiteManager`). The button is the design's one gold primary action and
 * opens a short note saying so. TODO(LED-1151): wire to a create-project API
 * once `backend/` exposes one over REST.
 */

import { useState } from "react";
import { H } from "../../tokens";

/** The Led AI glasses mark (the design system's `logo-mark.svg`). */
export function GlassesMark({ height = 34, color = H.ink }: { height?: number; color?: string }) {
  return (
    <svg viewBox="0 0 64 32" height={height} width={height * 2} fill="none" aria-hidden data-testid="glasses-mark" style={{ display: "block" }}>
      <circle cx="16" cy="16" r="13" stroke={color} strokeWidth="4" />
      <circle cx="48" cy="16" r="13" stroke={color} strokeWidth="4" />
      <path d="M29 16h6" stroke={color} strokeWidth="4" strokeLinecap="round" />
      <path d="M5 9 L1 3 M59 9 L63 3" stroke={color} strokeWidth="4" strokeLinecap="round" />
    </svg>
  );
}

export function NewProjectNote({ onClose }: { onClose: () => void }) {
  return (
    <div
      role="status"
      data-testid="new-project-note"
      style={{ marginTop: 14, maxWidth: 380, padding: "12px 14px", borderRadius: 12, background: H.white, border: `1px solid ${H.edge}`, fontSize: 13, lineHeight: 1.5, color: H.text, textAlign: "left" }}
    >
      Creating projects from the web is on its way. For now, add one in the Led AI app — it shows up here as soon as it syncs.{" "}
      <button type="button" onClick={onClose} style={{ border: 0, background: "transparent", padding: 0, font: "inherit", fontWeight: 700, color: H.ink, cursor: "pointer" }}>
        Got it
      </button>
    </div>
  );
}

export function HomeEmpty({ connectHref, onNavigate }: { connectHref: string; onNavigate: (href: string) => void }) {
  const [note, setNote] = useState(false);
  return (
    <div
      data-testid="home-first-run"
      style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", marginTop: 56 }}
    >
      <GlassesMark height={30} color={H.ink} />
      <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: "-0.5px", marginTop: 18 }}>No projects yet</div>
      <div style={{ fontSize: 14, color: H.sub, marginTop: 6, maxWidth: 420, lineHeight: 1.5 }}>
        Create a project here. Walks are recorded in the mobile app and show up on this page
      </div>
      <button
        type="button"
        className="kb press"
        data-testid="first-run-new-project"
        onClick={() => setNote(true)}
        style={{ marginTop: 20, display: "inline-flex", alignItems: "center", gap: 8, height: 40, padding: "0 18px", borderRadius: 10, border: 0, background: H.gold, color: H.ink, font: "inherit", fontSize: 14, fontWeight: 700, cursor: "pointer" }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={H.ink} strokeWidth="2.4" strokeLinecap="round" aria-hidden>
          <path d="M12 5v14M5 12h14" />
        </svg>
        New project
      </button>
      {note && <NewProjectNote onClose={() => setNote(false)} />}
      <a
        href={connectHref}
        className="kb hlink"
        data-testid="first-run-connect"
        onClick={(event) => {
          event.preventDefault();
          onNavigate(connectHref);
        }}
        style={{ marginTop: 14, fontSize: 13, color: H.sub }}
      >
        Using Procore or Autodesk? <span style={{ fontWeight: 700, color: H.ink }}>Connect it</span>
      </a>
    </div>
  );
}
